<template>
    <div class="pa-4 fill-height d-flex flex-column flex-grow-1">
        <ScatterChart class="flex-lg-fill" ref="scatterChart" v-show="!isChartHidden"/>

        <AgGrid class="flex-grow-1" ref="schedulerAgGridRef" :fetchUrl="schedulerFetchUrl" :columnDefs="schedulerColumnDefs"
        :gridParams="schedulerGridParams" :getChartData="getChartData" :filterChartData="filterChartData" @on-grid-ready="onGridReady"
        :domLayout="gridDomLayout" />

        <div class="position-absolute bottom-0 right-0" style="z-index:9999;">
            <v-btn :icon="isChartHidden ? icons.mdiFullscreen : icons.mdiFullscreenExit"
            size="small" color="secondary" variant="elevated" @click="isChartHidden = !isChartHidden" />
        </div>
    </div>
</template>

<script setup>
import { inject, onMounted, ref, useTemplateRef } from 'vue';
import ScatterChart from '@/components/eCharts/ScatterChart.vue';
import AgGrid from '@/components/AgGrid.vue';

import { mdiFullscreen, mdiFullscreenExit } from '@mdi/js';
import { toast } from 'vue3-toastify';
import { useLogStore } from '@/stores/logStore';
import { useUserStore } from '@/stores/userStore';

const urlWebAPI = inject('urlWebAPI');
const translate = inject('translate');
const stopLoading = inject('stopLoading');
const logStore = useLogStore();
const userStore = useUserStore();

const scatterChart = ref(null);

const icons = {
    mdiFullscreen,
    mdiFullscreenExit,
};

const gridDomLayout = 'normal';
const chartData = ref(null);
const rowsUpdated = ref([]);
const isChartHidden = ref(false);

const schedulerGridRef = useTemplateRef('schedulerAgGridRef');
const schedulerFetchUrl = 'GetAgCronTab';
const schedulerColumnDefs = [
    {
        field: 'hours',
        headerName: translate('hours', 'column'),
        sort: 'asc',
    },
    {
        field: 'customer',
        headerName: translate('customer', 'column'),
        sort: 'asc',
    },
    {
        field: 'process',
        headerName: translate('batFile', 'column'),
        sort: 'asc',
    },
    {
        field: 'agBatchName',
        headerName: 'Batch',
        sort: 'asc',
    },
    {
        field: 'agBatchAction',
        headerName: translate('action', 'column'),
        sort: 'asc',
    },
    {
        field: 'agBatchEnabled',
        headerName: translate('active', 'column'),
        sort: 'asc',
        type: 'booleanEditable',
    },
    {
        field: 'play',
        headerName: '',
        type: 'playAction',
    },
];
const schedulerGridParams = {
    token: userStore.getUser().token,
    distributorId: userStore.getUser().distributorId,
    customerReference: null,
};

onMounted(() => {
    init();
});

function onActivate() {
    init();
}

function init(){
    schedulerGridRef.value.gridRef.api.setGridOption("loading", true);
    getChartData().then(() => {
        schedulerGridRef.value.getGridData().then(() => {
            schedulerGridRef.value.gridRef.api.setGridOption("loading", false);
        });
    })
}

function onGridReady(params){
    //params.api.setGridOption("loading", false);
}

function onCellClicked(params) {
    if (typeof params.data != 'undefined' && typeof params.colDef.field != 'undefined' && params.colDef.field == 'play')
        playSchedulerTask(params);
}

function playSchedulerTask(params) {
    let data = {
        filePath: params.data.filePath,
    };

    $.getData(urlWebAPI + 'PlaySchedulerTask', data)
        .then((exitCode) => {
            console.log('ExitCode is ', exitCode);
        })
        .catch(error => {
            logStore.log(error, 'error');
            toast.error('Error during executing PlaySchedulerTask.');
        });
}

function getChartData(params = null) {
    return fetch(urlWebAPI + 'GetCronTab')
        .then(result => result.json())
        .then(rowData => {
            chartData.value = generateChartData(rowData);

            if (params != null)
                filterChartData(params);
        })
        .catch(error => {
            stopLoading();
            toast.error('Error during executing CronTab data.');
        });
}

function generateChartData(rowData, isFiltering = false) {
    let chartDataResult = rowData;

    if (!isFiltering) {
        chartDataResult = chartDataResult.sort((a, b) => {
            if (a.customer < b.customer)
                return 1;

            if (a.customer > b.customer)
                return -1;

            return 0;
        });

        let day = new Date().getDay();
        let dayToString = day > 9 ? day : '0' + day;

        let month = new Date().getMonth() + 1;
        let monthToString = month > 9 ? month : '0' + month;

        chartDataResult = chartDataResult.map(d =>
            [
                d.time.replace('0001-01-01', new Date().getFullYear() + '-' + monthToString + '-' + dayToString),
                d.customer,
                d.process,
                d.hours,
                d.batches,
            ]
        );

        // SET SERIE CHART STYLE
        for (let i = 0; i < chartDataResult.length; i++) {
            let data = chartDataResult[i];

            let oneBatchDisabled = false;
            let allBatchDisabled = false;

            if (data[4] != null && data[4].length > 0) {
                oneBatchDisabled = data[4].map(b => b.enabled).indexOf(false) > -1;
                allBatchDisabled = data[4].map(b => b.enabled).filter(e => e == false).length == data[4].map(b => b.enabled).length;
            }

            chartDataResult[i] = {
                name: (allBatchDisabled) ? translate('disabled', 'echarts') : (oneBatchDisabled) ? translate('manyDisabled', 'echarts') : translate('enabled', 'echarts'),
                value: data,
                itemStyle: {
                    color: (allBatchDisabled) ? '#cf6679' : (oneBatchDisabled) ? '#bb86fc' : '#03dac5',
                },
                emphasis: {
                    focus: 'series',
                    itemStyle: {
                        color: (allBatchDisabled) ? '#cf6679' : (oneBatchDisabled) ? '#bb86fc' : '#03dac5',
                    }
                },
            };
        }
    }

    // SPLIT DATA BY STATE
    let states = [translate('disabled', 'echarts'), translate('manyDisabled', 'echarts'), translate('enabled', 'echarts')];
    scatterChart.value.option.series = [];

    for (let j = 0; j < states.length; j++) {
        let datas = chartDataResult.filter(d => d.name == states[j]);

        scatterChart.value.option.series.push({
            name: states[j],
            data: datas,
            selectMode: 'multiple',
            symbol: 'circle',
            symbolSize: 10,
            type: 'scatter',
        });
    }

    scatterChart.value.needRefresh = true;

    return chartDataResult;
}

function filterChartData(params) {
    let filterModel = params.api.getFilterModel();
    let filteredColumns = Object.keys(filterModel);
    let chartDataFiltered = chartData.value;

    for (let i = 0; i < filteredColumns.length; i++) {
        let columnId = filteredColumns[i];
        let columnFilter = filterModel[columnId];

        if (typeof columnFilter != 'undefined') {
            switch (columnId) {
                case 'hours':
                    chartDataFiltered = chartDataFiltered.filter(c => columnFilter.values.indexOf(c.value[3]) > -1);
                    break;

                case 'customer':
                    chartDataFiltered = chartDataFiltered.filter(c => columnFilter.values.indexOf(c.value[1]) > -1);
                    break;

                case 'process':
                    chartDataFiltered = chartDataFiltered.filter(c => columnFilter.values.indexOf(c.value[2]) > -1);
                    break;

                case 'agBatchName':
                    chartDataFiltered = filterByBatch(chartDataFiltered, columnFilter, 'name');
                    break;

                case 'agBatchAction':
                    chartDataFiltered = filterByBatch(chartDataFiltered, columnFilter, 'action');
                    break;

                case 'agBatchEnabled':
                    chartDataFiltered = filterByBatch(chartDataFiltered, columnFilter, 'enabled');
                    break;
            }
        }
    }

    generateChartData(chartDataFiltered, true);
    stopLoading();
}

function filterByBatch(chartDataToFilter, columnFilter, field) {
    let tempData = new Array(chartDataToFilter)[0];
    chartDataToFilter = [];

    for (let j = 0; j < columnFilter.values.length; j++) {
        let filterValue = columnFilter.values[j];

        // BOOLEAN CASE
        filterValue = (filterValue == 'true') ? true : filterValue;
        filterValue = (filterValue == 'false') ? false : filterValue;

        let correspondingDatas = (filterValue != null) ? tempData.filter(c => c.value[4] != null && c.value[4].length > 0 && c.value[4].map(d => d[field]).indexOf(filterValue) > -1) : tempData.filter(c => c.value[4] == null || c.value[4].length == 0);

        correspondingDatas.forEach((data, index) => {
            chartDataToFilter.push(data);
        });
    }

    return chartDataToFilter;
}

defineExpose({ onCellClicked, rowsUpdated, getChartData, onActivate});
</script>

<style scoped>
    .scheduler-fullscreen-btn {
        position: absolute;
        right: 24px;
        bottom: 8px;
    }
</style>
